<script lang="ts">
import { defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import { loadDomains } from "@api/roster";
import type { RosterType } from "@interfaces/roster_type";
import type { StructureType } from "@interfaces/structure_type";
import type { DomainType } from "@interfaces/domain_type";
import { useLocalizedNamesStore } from "../../../stores/localizedNames";

interface ComponentData {
  domains: DomainType[] | null;
}

export default defineComponent({
  name: "SelectDomain",
  props: {
    roster: {
      type: Object as PropType<RosterType>,
      required: true,
    },
    structure: {
      type: Object as PropType<StructureType>,
      required: true,
    },
  },
  setup() {
    const i18n = useI18n();
    const localizedNamesStore = useLocalizedNamesStore();
    return {
      t: i18n.t,
      locale: i18n.locale,
      localizedNamesStore,
    };
  },
  data(): ComponentData {
    return {
      domains: null,
    };
  },
  beforeMount() {
    this.loadDomains();
  },
  watch: {
    locale() {
      // When locale switches without page reload, refresh displayed names.
      this.ensureDomainNamesLoaded();
    },
    domains() {
      this.ensureDomainNamesLoaded();
    },
  },
  methods: {
    selectDomain(domain: DomainType) {
      this.structure.domain = domain;
      this.$emit("select-step", "selectFaction");
    },
    domainName(domain: DomainType): string {
      const id = domain.id ?? undefined;
      return (
        (id ? this.localizedNamesStore.getName("domain", undefined, id) : null) ||
        domain.name ||
        ""
      );
    },
    async ensureDomainNamesLoaded(): Promise<void> {
      if (!this.domains?.length) return;

      const items = this.domains
        .filter((d) => d.id !== null)
        .map((d) => ({ type: "domain" as const, id: d.id as number }));

      await this.localizedNamesStore.ensureNamesLoaded(items, this.locale);
    },
    async loadDomains(): Promise<void> {
      if (this.domains === null) {
        this.domains = await loadDomains();
        await this.ensureDomainNamesLoaded();
      }
    },
  },
});
</script>

<template>
  <div class="roster-builder">
    <h1 class="roaster-title">{{ t("select_domain.title") }}</h1>
    <template v-if="domains">
      <div
        v-for="domain in domains"
        :key="domain.id"
        class="element domain w-20"
        :class="{ active: structure?.domain?.id === domain.id }"
        @click="selectDomain(domain)"
      >
        <div v-if="domain.image_path" class="domain-image">
          <img
            v-image-fallback="domain.image_path"
            class="img-fluid img-domain"
            alt="Domain image"
          />
        </div>
        <h2 class="text-center">{{ domainName(domain) }}</h2>
      </div>
    </template>
    <div v-else>{{ t("select_domain.loading") }}...</div>
  </div>
</template>
